import { autoFields } from "../constants";
import { DYNAMIC_FIELD_TYPES } from "../../common/constants";
import { removeAllTags } from "../../../config/utils";

export const getVisibilityConditionFields = formData => Object.values(formData ?? {}).reduce((acc, band) => ({ ...acc, ...band }), {});

const getConditionValue = condition => (Array.isArray(condition?.values) ? condition.values[0] : condition?.values);

const getConditionValueList = condition =>
    Array.isArray(condition?.values)
        ? condition.values
        : condition?.values !== "" && condition?.values !== null && condition?.values !== undefined
          ? [condition.values]
          : [];

const isSameConditionValue = (fieldValue, conditionValue) => `${fieldValue ?? ""}`?.toLowerCase() === `${conditionValue ?? ""}`?.toLowerCase();

const hasFieldValue = fieldValue =>
    Array.isArray(fieldValue) ? fieldValue.length > 0 : fieldValue !== "" && fieldValue !== null && fieldValue !== undefined;

const isNumericConditionValue = (fieldValue, conditionValue) =>
    fieldValue !== "" &&
    fieldValue !== null &&
    fieldValue !== undefined &&
    conditionValue !== "" &&
    conditionValue !== null &&
    conditionValue !== undefined &&
    !Number.isNaN(Number(fieldValue)) &&
    !Number.isNaN(Number(conditionValue));

const hasParentFieldValue = parentField => {
    if (!parentField) return false;
    const { value, uuid, field_type: fieldType } = parentField;

    if (["select", "custom_select"].includes(fieldType)) {
        return !!(uuid || (value !== "" && value !== null && value !== undefined));
    }

    if (fieldType === "boolean") {
        return value !== "" && value !== null && value !== undefined;
    }

    return value !== "" && value !== null && value !== undefined;
};

const satisfiesParentFields = (field, allFields) => {
    const parentFieldKeys = field?.parent_fields ?? [];
    if (!parentFieldKeys?.length) return true;
    return parentFieldKeys.every(parentKey => hasParentFieldValue(Object.values(allFields ?? {}).find(item => parentKey === item?.field_name)));
};

const satisfiesVisibilityCondition = (field, allFields) => {
    const visibilityCondition = field?.visibility_condition;
    const conditions = visibilityCondition?.all ?? [];
    if (!conditions?.length) return true;

    const checkCondition = condition => {
        const conditionField = Object.values(allFields ?? {}).find(item => item?.field_name === condition?.field);
        const rawFieldValue =
            !autoFields.includes(condition?.field) && ["custom_select"].includes(conditionField?.field_type)
                ? conditionField?.invalid
                    ? conditionField?.value
                    : conditionField?.uuid
                : conditionField?.value;
        const isMemoField = conditionField?.field_type === DYNAMIC_FIELD_TYPES.MEMO;
        const cleanMemoValue = value => (isMemoField && typeof value === "string" ? removeAllTags(value).trim() : value);
        const fieldValue = cleanMemoValue(rawFieldValue);
        const conditionValues = getConditionValueList(condition).map(cleanMemoValue);
        const conditionValue = cleanMemoValue(getConditionValue(condition));

        if (!conditionField || !condition?.operator || !conditionValues?.length) return false;
        if (condition?.operator === "in") {
            return conditionValues.some(value => isSameConditionValue(fieldValue, value));
        }
        if (condition?.operator === "not_in") {
            return hasFieldValue(fieldValue) && !conditionValues.some(value => isSameConditionValue(fieldValue, value));
        }
        if (condition?.operator === "equal") {
            return isSameConditionValue(fieldValue, conditionValue);
        }
        if (condition?.operator === "not_equal") {
            return hasFieldValue(fieldValue) && !isSameConditionValue(fieldValue, conditionValue);
        }
        if (condition?.operator === "contains") {
            return `${fieldValue ?? ""}`.toLowerCase().includes(`${conditionValue ?? ""}`.toLowerCase());
        }
        if (!isNumericConditionValue(fieldValue, conditionValue)) return false;
        if (condition?.operator === "greater_than") {
            return Number(fieldValue) > Number(conditionValue);
        }
        if (condition?.operator === "less_than") {
            return Number(fieldValue) < Number(conditionValue);
        }
        if (condition?.operator === "less_than_or_equal_to") {
            return Number(fieldValue) <= Number(conditionValue);
        }
        if (condition?.operator === "greater_than_or_equal_to") {
            return Number(fieldValue) >= Number(conditionValue);
        }
        return false;
    };

    return visibilityCondition?.operator === "or" ? conditions.some(checkCondition) : conditions.every(checkCondition);
};

export const shouldShowField = (field, allFields = {}) => {
    if (!satisfiesParentFields(field, allFields)) return false;
    return satisfiesVisibilityCondition(field, allFields);
};
